import { z } from 'zod';
import { LocationSchema, PriceSchema } from './base';
import { ActivitySchema, RestaurantSchema } from './google';

// Enhanced Itinerary Schemas with timeline support and comprehensive attributes
const ItineraryItemBaseSchema = z.object({
  id: z.string(),
  type: z
    .enum([
      'flight',
      'hotel',
      'restaurant',
      'activity',
      'travel',
      'custom',
    ])
    .default('custom'),
  title: z.string(),
  description: z.string().optional().default(''),
  startTime: z.date(),
  endTime: z.date().optional().nullable(),
  // Timeline-specific fields
  duration: z.number().optional().default(60), // Duration in minutes for timeline rendering
  isPlaceholder: z.boolean().optional().default(false), // Flag for placeholder items
  placeholderType: z
    .enum(['flight', 'hotel', 'transfer', 'general'])
    .optional()
    .nullable(),
  // Location and details
  location: LocationSchema.optional().nullable(),

  // Enhanced notes and tips system
  notes: z.string().optional().default(''), // User notes and reminders
  tip: z.string().optional().default(''), // Expert tips and insider secrets (hybrid+)
  privateNotes: z.string().optional().default(''), // Private planner notes (full_planning)

  order: z.number(), // For drag-and-drop ordering

  // Timeline styling hints
  color: z.string().optional().default('#3B82F6'), // Default blue color

  // Enhanced booking and confirmation details
  bookingReference: z.string().optional().default(''),
  bookingUrl: z.string().optional().default(''),
  confirmationNumber: z.string().optional().default(''),
  externalBooking: z.boolean().optional().default(false), // Booked outside the platform
  cost: PriceSchema.optional().nullable(),
  costScale: z
    .enum([
      'PRICE_LEVEL_UNSPECIFIED',
      'PRICE_LEVEL_FREE',
      'PRICE_LEVEL_INEXPENSIVE',
      'PRICE_LEVEL_MODERATE',
      'PRICE_LEVEL_EXPENSIVE',
      'PRICE_LEVEL_VERY_EXPENSIVE',
    ])
    .optional()
    .nullable(), // Google Places price levels

  // Contact and support information
  contactInfo: z
    .object({
      phone: z.string().optional().default(''),
      email: z.string().optional().default(''),
      website: z.string().optional().default(''),
    })
    .optional()
    .nullable(),

  // Accessibility and special requirements
  accessibilityNotes: z.string().optional().default(''),
  specialRequirements: z.array(z.string()).optional().default([]),

  // Timing and logistics
  arrivalBuffer: z.number().optional().default(0), // Minutes to arrive early
  departureBuffer: z.number().optional().default(0), // Minutes to stay after
  travelTimeToNext: z.number().optional().default(0), // Estimated travel time to next item

  // Weather and seasonal considerations
  weatherDependent: z.boolean().optional().default(false),
  seasonalAvailability: z.array(z.string()).optional().default([]),

  // Created/modified tracking
  createdAt: z.date().optional().nullable(),
  updatedAt: z.date().optional().nullable(),
  createdBy: z.enum(['user', 'ai', 'planner']).optional().default('user'),

  // AI suggestion indicator
  suggestedByAI: z.boolean().optional().default(false),
});

const FlightItineraryItemSchema = ItineraryItemBaseSchema.extend({
  type: z.literal('flight'),
  seatAssignments: z
    .array(
      z.object({
        travelerId: z.string(),
        seat: z.string(),
      })
    )
    .optional()
    .default([]),
  // Flight-specific timeline properties
  departureAirport: z.string().optional().default(''),
  arrivalAirport: z.string().optional().default(''),
  airline: z.string().optional().default(''),
  flightNumber: z.string().optional().default(''),
  // Override duration calculation for flights
  duration: z.number().optional().default(240), // Default 4 hours for flights
  color: z.string().optional().default('#10B981'), // Green for flights

  // Enhanced flight-specific attributes
  terminal: z
    .object({
      departure: z.string().optional().default(''),
      arrival: z.string().optional().default(''),
    })
    .optional()
    .nullable(),
  gate: z
    .object({
      departure: z.string().optional().default(''),
      arrival: z.string().optional().default(''),
    })
    .optional()
    .nullable(),
  aircraftType: z.string().optional().default(''),
  classOfService: z.string().optional().default(''),
  mileageProgram: z
    .object({
      program: z.string().optional().default(''),
      memberNumber: z.string().optional().default(''),
    })
    .optional()
    .nullable(),
  specialServices: z.array(z.string()).optional().default([]), // Wheelchair, meal preferences, etc.
  baggage: z
    .object({
      checkedAllowance: z.string().optional().default(''),
      carryOnAllowance: z.string().optional().default(''),
      additionalBags: z.number().optional().default(0),
    })
    .optional()
    .nullable(),
  checkInInfo: z
    .object({
      onlineCheckInUrl: z.string().optional().default(''),
      checkInTime: z.string().optional().default(''), // "2 hours before"
      reminderSet: z.boolean().optional().default(false),
    })
    .optional()
    .nullable(),
});

const HotelItineraryItemSchema = ItineraryItemBaseSchema.extend({
  type: z.literal('hotel'),
  roomNumber: z.string().optional().nullable(),
  checkInTime: z.date().optional().nullable(),
  checkOutTime: z.date().optional().nullable(),
  // Hotel-specific properties
  hotelName: z.string().optional().default(''),
  roomType: z.string().optional().default(''),
  // Timeline properties for hotel stays
  duration: z.number().optional().default(30), // Check-in/out duration
  color: z.string().optional().default('#8B5CF6'), // Purple for hotels

  // Enhanced hotel-specific attributes
  starRating: z.number().min(1).max(5).optional().nullable(),
  propertyType: z
    .enum([
      'hotel',
      'resort',
      'bed_breakfast',
      'apartment',
      'hostel',
      'villa',
      'other',
    ])
    .optional()
    .nullable(),
  amenities: z.array(z.string()).optional().default([]), // Pool, gym, spa, wifi, etc.
  bedConfiguration: z
    .object({
      bedType: z.string().optional().default(''), // King, Queen, Twin, etc.
      bedCount: z.number().optional().default(1),
    })
    .optional()
    .nullable(),
  guestDetails: z
    .object({
      adults: z.number().optional().default(1),
      children: z.number().optional().default(0),
      infants: z.number().optional().default(0),
    })
    .optional()
    .nullable(),
  mealPlan: z.string().optional().default(''), // Room only, breakfast included, etc.
  loyaltyProgram: z
    .object({
      program: z.string().optional().default(''),
      memberNumber: z.string().optional().default(''),
      tierStatus: z.string().optional().default(''),
    })
    .optional()
    .nullable(),
  cancellationPolicy: z.string().optional().default(''),
  checkInInstructions: z.string().optional().default(''),
  parkingInfo: z
    .object({
      available: z.boolean().optional().default(false),
      type: z.string().optional().default(''), // Valet, self-park, street
      cost: PriceSchema.optional().nullable(),
    })
    .optional()
    .nullable(),
  earlyCheckIn: z.boolean().optional().default(false),
  lateCheckOut: z.boolean().optional().default(false),
});

const RestaurantItineraryItemSchema = ItineraryItemBaseSchema.extend({
  type: z.literal('restaurant'),
  restaurant: RestaurantSchema.optional().nullable(),
  reservationTime: z.date().optional().nullable(),
  partySize: z.number().optional().nullable(),
  specialRequests: z.string().optional().default(''),
  // Restaurant-specific properties
  cuisineType: z.string().optional().default(''),
  mealType: z
    .enum(['breakfast', 'lunch', 'dinner', 'snack', 'drinks'])
    .optional()
    .nullable(),
  // Timeline properties
  duration: z.number().optional().default(90), // Default 1.5 hours for meals
  color: z.string().optional().default('#F59E0B'), // Amber for restaurants

  // Enhanced restaurant-specific attributes
  priceRange: z.enum(['$', '$$', '$$$', '$$$$']).optional().nullable(),
  dressCode: z.string().optional().default(''),
  reservable: z.boolean().optional().default(false),
  reservationConfirmed: z.boolean().optional().default(false),
  dietaryAccommodations: z.array(z.string()).optional().default([]), // Vegetarian, gluten-free, etc.
  tablePreferences: z
    .object({
      seating: z.string().optional().default(''), // Window, outdoor, bar, etc.
      occasion: z.string().optional().default(''), // Birthday, anniversary, etc.
    })
    .optional()
    .nullable(),
  menuRecommendations: z.array(z.string()).optional().default([]), // Signature dishes, must-try items
  localTips: z.string().optional().default(''), // Insider knowledge about ordering, timing, etc.
  averageWaitTime: z.number().optional().default(0), // Minutes typically waited
  peakHours: z.array(z.string()).optional().default([]), // Busy time periods
  paymentMethods: z.array(z.string()).optional().default([]),
  serviceCharge: z
    .object({
      included: z.boolean().optional().default(false),
      percentage: z.number().optional().nullable(),
    })
    .optional()
    .nullable(),
  kidsFriendly: z.boolean().optional().default(false),
  noiseLevel: z.enum(['quiet', 'moderate', 'loud']).optional().nullable(),
});

const ActivityItineraryItemSchema = ItineraryItemBaseSchema.extend({
  type: z.literal('activity'),
  activity: ActivitySchema.optional().nullable(),
  ticketQuantity: z.number().optional().nullable(),
  // Activity-specific properties
  category: z.string().optional().default(''),
  difficultyLevel: z
    .enum(['easy', 'moderate', 'difficult'])
    .optional()
    .nullable(),
  minAge: z.number().optional().nullable(),
  maxGroupSize: z.number().optional().nullable(),
  requiresBooking: z.boolean().optional().default(false),
  // Timeline properties - activities can have variable durations
  duration: z.number().optional().default(120), // Default 2 hours for activities
  estimatedDuration: z
    .object({
      min: z.number(),
      max: z.number(),
    })
    .optional()
    .nullable(),
  color: z.string().optional().default('#EF4444'), // Red for activities

  // Enhanced activity-specific attributes
  operatingHours: z
    .object({
      openTime: z.string().optional().default(''),
      closeTime: z.string().optional().default(''),
      days: z.array(z.string()).optional().default([]),
    })
    .optional()
    .nullable(),
  ticketInfo: z
    .object({
      pricePerPerson: PriceSchema.optional().nullable(),
      bookingRequired: z.boolean().optional().default(false),
      advanceBookingDays: z.number().optional().default(0),
      cancellationPolicy: z.string().optional().default(''),
      ticketUrl: z.string().optional().default(''),
    })
    .optional()
    .nullable(),
  whatToExpect: z.string().optional().default(''),
  whatToBring: z.array(z.string()).optional().default([]), // Comfortable shoes, camera, etc.
  fitnessLevel: z.enum(['low', 'moderate', 'high']).optional().nullable(),
  groupSizeRecommendation: z.string().optional().default(''),
  bestTimeToVisit: z
    .object({
      timeOfDay: z.string().optional().default(''), // Morning, afternoon, evening
      season: z.string().optional().default(''),
      weatherConditions: z.string().optional().default(''),
    })
    .optional()
    .nullable(),
  photoOpportunities: z.array(z.string()).optional().default([]),
  insiderSecrets: z.string().optional().default(''), // Hidden viewpoints, less crowded times, etc.
  safetyConsiderations: z.string().optional().default(''),
  alternativeOptions: z.array(z.string()).optional().default([]), // Backup plans if closed/full
  languagesAvailable: z.array(z.string()).optional().default([]),
  includesGuide: z.boolean().optional().default(false),
  transportationProvided: z.boolean().optional().default(false),
  meetingPoint: z
    .object({
      name: z.string().optional().default(''),
      instructions: z.string().optional().default(''),
      coordinates: z
        .object({
          lat: z.number(),
          lng: z.number(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
});

const TravelItineraryItemSchema = ItineraryItemBaseSchema.extend({
  type: z.literal('travel'),
  travelMode: z.enum(['flight', 'train', 'drive', 'bus', 'ferry', 'walk']),
  departure: z
    .object({
      location: z.string().optional().default(''),
      time: z.date().optional().nullable(),
    })
    .optional()
    .default({ location: '', time: null }),
  arrival: z
    .object({
      location: z.string().optional().default(''),
      time: z.date().optional().nullable(),
    })
    .optional()
    .default({ location: '', time: null }),
  carrier: z.string().optional().default(''),
  referenceNumber: z.string().optional().default(''),
  duration: z.number().optional().default(120),
  color: z.string().optional().default('#10B981'),
});

const CustomItineraryItemSchema = ItineraryItemBaseSchema.extend({
  type: z.literal('custom'),
  customData: z.record(z.any(), z.any()).optional().nullable(),
  // Custom items should have flexible properties
  category: z.string().optional().default('general'),
  duration: z.number().optional().default(60), // Default 1 hour
  color: z.string().optional().default('#6B7280'), // Gray for custom items
});

const ItineraryItemSchema = z.discriminatedUnion('type', [
  FlightItineraryItemSchema,
  HotelItineraryItemSchema,
  RestaurantItineraryItemSchema,
  ActivityItineraryItemSchema,
  TravelItineraryItemSchema,
  CustomItineraryItemSchema,
]);

const DailyItinerarySchema = z.object({
  title: z.string(),
  date: z.date(),
  location: LocationSchema.optional().nullable(),
  estimatedCost: PriceSchema.optional().nullable(),
  items: z.array(ItineraryItemSchema),
  notes: z.string().optional().default(''),
  // Timeline view properties
  timelineConfig: z
    .object({
      startHour: z.number().min(0).max(23).optional().default(6), // Timeline starts at 6 AM
      endHour: z.number().min(0).max(23).optional().default(23), // Timeline ends at 11 PM
      intervalMinutes: z.number().optional().default(30), // 30-minute intervals
    })
    .optional()
    .nullable(),
  // Daily statistics
  totalDuration: z.number().optional().default(0), // Total scheduled minutes
  freeTime: z.number().optional().default(0), // Available free time in minutes
  travelTime: z.number().optional().default(0), // Estimated travel time between items
});

// Helper schemas for timeline rendering
const TimelineSlotSchema = z.object({
  startTime: z.date(),
  endTime: z.date(),
  duration: z.number(), // Duration in minutes
  item: ItineraryItemSchema.optional().nullable(),
  isEmpty: z.boolean().default(true),
  isConflict: z.boolean().default(false), // Overlapping items
});

const TimelineViewSchema = z.object({
  date: z.date(),
  slots: z.array(TimelineSlotSchema),
  totalSlots: z.number(),
  occupiedSlots: z.number(),
  utilizationPercentage: z.number().min(0).max(100),
});

// Export all schemas and inferred types
export {
  // Itinerary
  ItineraryItemBaseSchema,
  FlightItineraryItemSchema,
  HotelItineraryItemSchema,
  RestaurantItineraryItemSchema,
  ActivityItineraryItemSchema,
  TravelItineraryItemSchema,
  CustomItineraryItemSchema,
  ItineraryItemSchema,
  DailyItinerarySchema,
  // Timeline support
  TimelineSlotSchema,
  TimelineViewSchema,
};

export type ItineraryItemBase = z.infer<typeof ItineraryItemBaseSchema>;
export type FlightItineraryItem = z.infer<typeof FlightItineraryItemSchema>;
export type HotelItineraryItem = z.infer<typeof HotelItineraryItemSchema>;
export type RestaurantItineraryItem = z.infer<
  typeof RestaurantItineraryItemSchema
>;
export type ActivityItineraryItem = z.infer<typeof ActivityItineraryItemSchema>;
export type TravelItineraryItem = z.infer<typeof TravelItineraryItemSchema>;
export type CustomItineraryItem = z.infer<typeof CustomItineraryItemSchema>;
export type ItineraryItem = z.infer<typeof ItineraryItemSchema>;
export type DailyItinerary = z.infer<typeof DailyItinerarySchema>;

// Timeline types
export type TimelineSlot = z.infer<typeof TimelineSlotSchema>;
export type TimelineView = z.infer<typeof TimelineViewSchema>;

// Utility type for drag-and-drop operations with timeline support
export type DragDropItem = ItineraryItem & {
  dragId: string;
  isDragging?: boolean;
  timelinePosition?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
};

// Helper type for placeholder items
export type PlaceholderItem = ItineraryItem & {
  isPlaceholder: true;
  placeholderType: 'flight' | 'hotel' | 'transfer' | 'general';
};

// Timeline configuration type
export type TimelineConfig = {
  startHour: number;
  endHour: number;
  intervalMinutes: number;
  pixelsPerHour: number;
  showTimeLabels: boolean;
  compactView: boolean;
};
